/* cwclientelements
   copyright 2019 LOGILAB S.A. (Paris, FRANCE), all rights reserved.
   contact http://www.logilab.fr/ -- mailto:contact@logilab.fr
*/

import * as React from 'react';
import {client, providers} from '@logilab/cwclientlibjs';
import {
    AttributeValueEvent,
    TranslationContext,
    ProvidersContext,
    isRichString,
} from './commons';
import {RichStringEntityAttribute} from './RichStringEntityAttribute';
import {BooleanEntityAttribute} from './BooleanEntityAttribute';
import {IntegerEntityAttribute} from './IntegerEntityAttribute';
import {FloatEntityAttribute} from './FloatEntityAttribute';
import {DateEntityAttribute} from './DateEntityAttribute';
import {DefaultEntityAttribute} from './DefaultEntityAttribute';
import {MergedEntityRelationsGroup} from './MergedEntityRelationsGroup';

import './css/EntityForm.css';

/**
 * Base interface for components for entity attributes
 */
export class EntityForm<
    P extends EntityFormProps = EntityFormProps,
    S extends EntityFormState = EntityFormState
> extends React.Component<P, S> {
    public static contextType = ProvidersContext;
    public context!: React.ContextType<typeof ProvidersContext>;
    /**
     * Whether the component is currently mounted
     */
    _isMounted: boolean;
}

/**
 * The parameters for an entity view component
 */
export interface EntityFormProps {
    /**
     * The timestamp on these parameters
     */
    timestamp: number;
    /**
     * The EID of the entity of interest
     */
    eid: number;
    /**
     * The CSS class name(s) for the component in normal conditions
     */
    className: string;
    /**
     * Whether to enable the edition of the entity
     */
    editable: boolean;
    /**
     * The attributes name white list to render
     * If this list is null then all attributes are rendered
     */
    attributesWhiteList: string[] | null;
    /**
     * The relations name white list to render
     * If this list is null then all relations are rendered
     */
    relationsWhiteList: string[] | null;
    /**
     * Handler of events when the user click on a link to an entity
     */
    onClickGotoTarget: (eid: number) => void;
    /**
     * Handler of events when the user wants to edit the relations
     */
    onClickEdit: (
        data: providers.Entity,
        relations: providers.EntityRelationSchema[]
    ) => void;
    /**
     * Gets the target link for the specified entity
     * @param eid The entity
     */
    getTargetLink: (eid: number) => string;
    /**
     * Handler of events when a new value input has been received
     */
    onChangeAttribute: AttributeValueEvent;
    /**
     * Handler when an attribute has been saved in the database
     * if editable is true
     */
    attributeHasBeenSaved: (
        entity: providers.Entity,
        attribute: providers.EntityAttributeSchema,
        value: any
    ) => void;
    /**
     * Handler when an attribute has not been saved in the database
     * because of an error
     * if editable is true
     */
    attributeSavingError: (
        entity: providers.Entity,
        attribute: providers.EntityAttributeSchema,
        value: any,
        error: string
    ) => void;
    /**
     * Renders the attributes and relations of this entity
     * @param component The current component (this)
     * @param data The entity
     * @param schema The schema of the entity
     */
    renderElements: (
        component: EntityForm,
        data: providers.Entity,
        schema: providers.EntitySchema
    ) => JSX.Element[];
    /**
     * Renders the attributes of this entity
     * @param component The current component (this)
     * @param data The entity
     * @param schema The schema of the entity
     */
    renderAttributes: (
        component: EntityForm,
        data: providers.Entity,
        schema: providers.EntitySchema
    ) => JSX.Element[];
    /**
     * Renders an attribute of this entity
     * @param component The current component (this)
     * @param data The entity
     * @param attribute The schema of the attribute to be rendered
     */
    renderAttribute: (
        component: EntityForm,
        data: providers.Entity,
        attribute: providers.EntityAttributeSchema
    ) => JSX.Element;
    /**
     * Renders a group of relations of this entity
     * @param component The current component (this)
     * @param data The entity
     * @param relations The schema of the relations to be rendered
     * @param entityIsSubject Whether the entity is a subject of the relations (or an object)
     */
    renderRelationsGroup: (
        component: EntityForm,
        data: providers.Entity,
        relations: providers.EntityRelationSchema[],
        entityIsSubject: boolean
    ) => JSX.Element[];
    /**
     * Gets the displayable value for the specified entity and schema
     * @param entity The entity that will be rendered
     * @param schema The schema for the entity
     */
    renderDisplayValue: (
        entity: providers.Entity,
        schema: providers.EntitySchema
    ) => JSX.Element;
    /**
     * Renders the entity's title
     * @param component The current component (this)
     * @param data The entity
     * @param schema The schema of the entity
     */
    renderTitle: (
        component: EntityForm,
        data: providers.Entity,
        schema: providers.EntitySchema
    ) => JSX.Element;
    /**
     * Renders this view when data is still loading
     * @param component The current component (this)
     */
    renderLoading: (component: EntityForm) => JSX.Element;
    /**
     * Renders this view when there are no data
     * @param component The current component (this)
     */
    renderNoData: (component: EntityForm) => JSX.Element;
    /**
     * Renders this view when there are no data
     * @param component The current component (this)
     */
    renderOnError: (component: EntityForm) => JSX.Element;
    /**
     * Renders the loaded data
     * @param component The current component (this)
     * @param data The entity
     * @param schema The schema of the entity
     */
    renderLoaded: (
        component: EntityForm,
        data: providers.Entity,
        schema: providers.EntitySchema
    ) => JSX.Element;
    /**
     * Renders the component
     * @param component The current component (this)
     */
    render: (component: EntityForm) => JSX.Element;
}

/**
 * The status of an entity view
 */
export enum EntityStatus {
    /**
     * The view is currently loading its data
     */
    loading = 'loading',
    /**
     * The view has attempted to load its data but there was none
     */
    nodata = 'nodata',
    /**
     * The data have been loaded and are ready
     */
    withdata = 'withdata',
    /**
     * There was an error while loading the view
     */
    onerror = 'onerror',
}

/**
 * State for an entity view component
 */
export interface EntityFormState {
    /**
     * The current status of the view
     */
    status: EntityStatus;
    /**
     * The current error message, if any
     */
    error: string | null;
    /**
     * The entity, of null when it is still being loaded
     */
    data: providers.Entity | null;
    /**
     * The schema for the entity, or null when it is still being loaded
     */
    schema: providers.EntitySchema | null;
}

/**
 * The defaults props for an entity view
 */
export const ENTITY_DEFAULT_PROPS = {
    className: '',
    editable: false,
    attributesWhiteList: null,
    relationsWhiteList: null,
    onClickGotoTarget: (eid: number) => {
        /* do nothing */
    },
    onClickEdit: (
        data: providers.Entity,
        relations: providers.EntityRelationSchema[]
    ) => {
        /* do nothing */
    },
    getTargetLink: (eid: number) => `/${eid}`,
    renderElements: (
        component: EntityForm,
        data: providers.Entity,
        schema: providers.EntitySchema
    ) => {
        let result = [];
        let elemements = component.props.renderAttributes(
            component,
            data,
            schema
        );
        result.push(...elemements);
        let relationsTo = getRelationGroups(schema.relationsTo);
        for (let relations of relationsTo) {
            let elements = component.props.renderRelationsGroup(
                component,
                data,
                relations,
                true
            );
            result.push(...elements);
        }
        let relationsFrom = getRelationGroups(schema.relationsFrom);
        for (let relations of relationsFrom) {
            let elements = component.props.renderRelationsGroup(
                component,
                data,
                relations,
                false
            );
            result.push(...elements);
        }
        return result;
    },
    renderAttributes: (
        component: EntityForm,
        data: providers.Entity,
        schema: providers.EntitySchema
    ) => {
        return schema.attributes
            .filter(
                (attribute: providers.EntityAttributeSchema) =>
                    component.props.attributesWhiteList === null ||
                    component.props.attributesWhiteList.includes(attribute.name)
            )
            .map((attribute: providers.EntityAttributeSchema, index: number) =>
                component.props.renderAttribute(component, data, attribute)
            );
    },
    onChangeAttribute: (
        entity: providers.Entity,
        attribute: providers.EntityAttributeSchema,
        required: boolean,
        value: any,
        rqlClient?: client.RqlClient
    ) => null,
    renderAttribute: (
        component: EntityForm,
        data: providers.Entity,
        attribute: providers.EntityAttributeSchema
    ) => {
        const props = {
            key: data['@eid'] + '_' + attribute.name,
            entity: data,
            attribute: attribute,
            disabled: component.props.editable === false,
            onChange: component.props.onChangeAttribute,
            rqlClient: component.context.rqlClient,
            attributeHasBeenSaved: component.props.attributeHasBeenSaved,
            attributeSavingError: component.props.attributeSavingError,
        };
        if (attribute.type === 'Boolean') {
            return <BooleanEntityAttribute {...props} />;
        } else if (attribute.type === 'Int') {
            return <IntegerEntityAttribute {...props} />;
        } else if (attribute.type === 'Float' || attribute.type === 'Decimal') {
            return <FloatEntityAttribute {...props} />;
        } else if (
            isRichString(attribute, component.state.schema?.attributes)
        ) {
            return (
                <RichStringEntityAttribute
                    {...props}
                    format={data[attribute.name + '_format']}
                />
            );
        } else if (['Date', 'TZDatetime'].includes(attribute.type)) {
            return <DateEntityAttribute {...props} />;
        } else {
            return <DefaultEntityAttribute {...props} />;
        }
    },
    attributeHasBeenSaved: (
        entity: providers.Entity,
        attribute: providers.EntityAttributeSchema,
        value: any
    ) => {
        /* do nothing */
    },
    attributeSavingError: (
        entity: providers.Entity,
        attribute: providers.EntityAttributeSchema,
        value: any,
        error: string
    ) => {
        /* do nothing */
    },
    renderRelationsGroup: (
        component: EntityForm,
        data: providers.Entity,
        relations: providers.EntityRelationSchema[],
        entityIsSubject: boolean
    ) => {
        const filteredRelations =
            component.props.relationsWhiteList !== null
                ? relations.filter((rel) =>
                      component.props.relationsWhiteList!.includes(rel.name)
                  )
                : relations;
        if (filteredRelations.length === 0) {
            return [];
        }
        return [
            <MergedEntityRelationsGroup
                key={`${data.eid}_${filteredRelations[0].name}`}
                entity={data}
                relations={filteredRelations}
                entityIsSubject={entityIsSubject}
                editable={component.props.editable}
                onClickGotoTarget={component.props.onClickGotoTarget}
                onClickEdit={(relations) =>
                    component.props.onClickEdit(data, relations)
                }
                getTargetLink={component.props.getTargetLink}
                renderDisplayValue={component.props.renderDisplayValue}
            />,
        ];
    },
    renderDisplayValue: (
        entity: providers.Entity,
        schema: providers.EntitySchema
    ) => {
        return <span>{providers.getTitleFor(entity, schema)}</span>;
    },
    renderTitle: (
        component: EntityForm,
        data: providers.Entity,
        schema: providers.EntitySchema
    ) => {
        return <h1>{`${schema.name} : ${data[schema.attributes[0].name]}`}</h1>;
    },
    renderLoading: (component: EntityForm) => {
        return (
            <TranslationContext.Consumer>
                {(tc) => (
                    <div className={component.props.className}>
                        {tc.translate('loading')}
                    </div>
                )}
            </TranslationContext.Consumer>
        );
    },
    renderNoData: (component: EntityForm) => {
        return (
            <TranslationContext.Consumer>
                {(tc) => (
                    <div className={component.props.className}>
                        {tc.translate('no data')}
                    </div>
                )}
            </TranslationContext.Consumer>
        );
    },
    renderOnError: (component: EntityForm) => {
        return (
            <TranslationContext.Consumer>
                {(tc) => (
                    <div className={component.props.className}>
                        {tc.translate('error') +
                            ' : ' +
                            tc.translate(
                                component.state.error === null
                                    ? ''
                                    : component.state.error
                            )}
                    </div>
                )}
            </TranslationContext.Consumer>
        );
    },
    renderLoaded: (
        component: EntityForm,
        data: providers.Entity,
        schema: providers.EntitySchema
    ) => {
        return (
            <div className={'EntityForm ' + component.props.className}>
                {component.props.renderTitle(component, data, schema)}
                <table className="table table-condensed">
                    <tbody>
                        {component.props.renderElements(
                            component,
                            data,
                            schema
                        )}
                    </tbody>
                </table>
            </div>
        );
    },
    render: (component: EntityForm) => {
        if (component.state.status === EntityStatus.loading) {
            return component.props.renderLoading(component);
        } else if (component.state.status === EntityStatus.nodata) {
            return component.props.renderNoData(component);
        } else if (component.state.status === EntityStatus.onerror) {
            return component.props.renderOnError(component);
        } else if (
            component.state.schema === null ||
            component.state.data === null
        ) {
            // should have data, but no ...
            return component.props.renderNoData(component);
        }
        return component.props.renderLoaded(
            component,
            component.state.data,
            component.state.schema
        );
    },
};

/**
 * Groups the specified relations accord to a a criterion (by default, same name)
 * @param relations The relations to groups
 */
export function getRelationGroups(
    relations: providers.EntityRelationSchema[]
): providers.EntityRelationSchema[][] {
    let names: string[] = [];
    let groups: providers.EntityRelationSchema[][] = [];
    relations.forEach((relation: providers.EntityRelationSchema) => {
        let index = names.indexOf(relation.name);
        if (index >= 0) {
            groups[index].push(relation);
        } else {
            names.push(relation.name);
            groups.push([relation]);
        }
    });
    return groups;
}
